// src/components/Aside.jsx
import React, { useContext, useState } from "react";
import { useIntl } from "react-intl";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";

import {
  ProSidebar,
  Menu,
  MenuItem,
  SubMenu,
  SidebarHeader,
  SidebarFooter,
  SidebarContent,
} from "react-pro-sidebar";

import {
  Button,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Card,
  Box,
} from "@mui/material";

import {
  AccountCircle,
  Close,
  Dashboard,
  School,
  MenuBook,
  Person,
  GroupAdd,
  BusinessCenter,
  Equalizer,
  Settings,
  HelpRounded,
  ChevronLeft,
  ChevronRight,
  Menu as MenuIcon,
  AdminPanelSettings,
} from "@mui/icons-material";

import sidebarBg from "../../assets/bg2.jpg";
import perfil from "../../assets/perfil.png";
import "../Scss/stylesComponents.scss?v=1.1.6";

import { PermissionContext, UserContext } from "../Context/Context";
import { usePermissions } from "../Context/PermissionProvider";
import ChangeMode from "./ChangeMode";

function Aside({
  image,
  collapsed = false,
  rtl,
  toggled = false,
  handleToggleSidebar,
  handleCollapseSidebar,
  userLogged,
  isMobile = false,
  theme,
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { logout, userData } = useContext(UserContext);
  const location = useLocation();
  const { newRoleConfig, exitTestMode } = useContext(PermissionContext);

  const currentPath = location.pathname;
  const { can } = usePermissions();

  const [openDialog, setOpenDialog] = useState(false);
  const [openNewUserMode, setOpenNewUserMode] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState(null);

  const shortcuts = [
    { id: "create-contact", label: "Crear Contacto", path: "/contactos/crear" },
    {
      id: "create-expedient",
      label: "Crear Expediente",
      path: "/expedientes/crear",
    },
  ];

  const menuConfig = [
    {
      type: "submenu",
      id: "perfil",
      label: "perfil",
      icon: <AccountCircle />,
      items: [
        { id: "perfil-datos", label: "datos", path: "/perfil" },
        { id: "perfil-mensajes", label: "mensajes", path: "/mensajes" },
        { id: "perfil-logout", label: "cerrarSesion", action: "logout" },
      ],
    },
    {
      type: "menu",
      id: "main-1",
      items: [
        {
          id: "main-panel",
          icon: <Dashboard />,
          label: "panel",
          path: "/panel",
        },
        {
          id: "main-contactos",
          icon: <Person />,
          label: "contactos",
          path: "/contactos",
          module: "contacts",
          action: "view",
        },
        {
          id: "main-clientes",
          icon: <GroupAdd />,
          label: "clientes",
          path: "/clientes",
          module: "clients",
          action: "view",
        },
        {
          id: "main-expedientes",
          icon: <BusinessCenter />,
          label: "expedientes",
          path: "/expedientes",
          module: "cases",
          action: "view",
        },
      ],
    },
    {
      type: "submenu",
      id: "config",
      label: "configuracion",
      icon: <Settings />,
      items: [
        {
          id: "cfg-empresas",
          path: "/empresas",
          label: "empresas",
          module: "company",
          action: "view",
        },
        {
          id: "cfg-asociaciones",
          path: "/asociaciones",
          label: "asociaciones",
          module: "association",
          action: "view",
        },
        // {
        //   id: "cfg-despachos",
        //   path: "/despachos",
        //   label: "despachos",
        //   module: "office",
        //   action: "view",
        // },
        // {
        //   id: "cfg-grupos",
        //   path: "/grupos",
        //   label: "grupos",
        //   module: "group",
        //   action: "view",
        // },
        {
          id: "cfg-usuarios",
          path: "/usuarios",
          label: "usuarios",
          module: "users",
          action: "view",
        },
        {
          id: "cfg-roles",
          path: "/roles",
          label: "tiposDeUsuarios",
          module: "userstypes",
          action: "view",
        },
        {
          id: "cfg-cases",
          path: "/expedientes-config",
          label: "expedientesConfig",
          module: "cases",
          action: "edit",
        },
        {
          id: "cfg-tipos-casos",
          path: "/tiposdecasos",
          label: "tiposCasos",
          module: "casestypes",
          action: "view",
        },
        {
          id: "cfg-contratos",
          path: "/contratos",
          label: "hojasDeCargo",
          module: "contracts",
          action: "view",
        },
      ],
    },
    {
      type: "submenu",
      id: "informes",
      label: "informes",
      icon: <Equalizer />,
      items: [
        {
          id: "informe-general",
          label: "informeGeneral",
          path: "/informes/informe-general",
          module: "reports",
          action: "view",
        },
      ],
    },
    {
      type: "menu",
      id: "main-2",
      items: [
        {
          id: "main-biblioteca",
          icon: <MenuBook />,
          label: "biblioteca",
          path: "/biblioteca",
        },
        {
          id: "main-manual",
          icon: <School />,
          path: "/manual",
          label: "manual",
          href: "https://crm.consulting-fidelity.com/uploads/manual-crm-legal.pdf",
          disabled: true,
        },
      ],
    },
  ];

  const cerrarSesion = () => {
    logout(true);
  };

  const renderNavOrAnchor = (item) => {
    const label = intl.formatMessage({ id: item.label });

    if (item.disabled) {
      return <span className="menu-item-disabled">{label}</span>;
    }

    if (item.path) {
      return (
        <NavLink
          to={item.path}
          onClick={() => {
            setOpenSubMenu(null);
            if (isMobile) handleToggleSidebar(false);
          }}
          className={({ isActive }) =>
            isActive ? "menu-item-active" : undefined
          }>
          {label}
        </NavLink>
      );
    }

    if (item.href) {
      return (
        <a
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            setOpenSubMenu(null);
            if (isMobile) handleToggleSidebar(false);
          }}>
          {label}
        </a>
      );
    }

    if (item.action === "logout") {
      return (
        <span onClick={cerrarSesion} style={{ cursor: "pointer" }}>
          {label}
        </span>
      );
    }

    return <span>{label}</span>;
  };

  const DesktopToggle = () => (
    <div
      className={`sidebar-toggle-btn desktop-toggle ${
        collapsed ? "collapsed" : ""
      }`}
      onClick={() => handleCollapseSidebar && handleCollapseSidebar()}
      title={collapsed ? "Expandir barra" : "Minimizar barra"}>
      {collapsed ? <ChevronRight /> : <ChevronLeft />}
    </div>
  );

  // Mobile hamburger button (opens/closes sidebar)
  const MobileToggle = () => (
    <div
      className="sidebar-toggle-btn mobile-toggle"
      onClick={() => handleToggleSidebar && handleToggleSidebar(true)}
      title="Abrir menú">
      <MenuIcon />
    </div>
  );

  return (
    <div className={"sidebar-wrapper"} style={{}}>
      <ProSidebar
        rtl={rtl}
        collapsed={false}
        toggled={toggled}
        breakPoint="md"
        className={`proSideBarClass ${isMobile ? "mobile-top" : ""} ${
          !isMobile && collapsed ? "collapsed" : ""
        }`}
        style={{
          width: isMobile ? "100%" : collapsed ? "80px" : "",
          position: isMobile ? "fixed" : "relative",
          top: isMobile ? 0 : "auto",
          left: 0,
          height: isMobile ? "100vh" : "100%",
          zIndex: isMobile ? 1400 : "auto",
          transform: isMobile
            ? toggled
              ? "translateY(0)"
              : "translateY(-100%)"
            : "none",
          transition: "width 0.5s ease, transform 0.35s ease",
        }}>
        {isMobile && toggled && (
          <div
            onClick={() => handleToggleSidebar(false)}
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.45)",
            }}
          />
        )}

        <SidebarHeader>
          {isMobile && (
            <IconButton
              onClick={() => handleToggleSidebar(false)}
              sx={{
                position: "absolute",
                right: 10,
                top: 10,
                color: "white",
              }}>
              <Close />
            </IconButton>
          )}

          <div className="sidebarHeaderClass">
            {userLogged?.name && !collapsed ? (
              <div className="userInfo">
                <img src={perfil} alt="Perfil" width="50px" />
                <div
                  style={{
                    width: "100%",
                    padding: "10px",
                    textAlign: "center",
                  }}>
                  <div className="usernameSidebar">
                    <span style={{ fontSize: "12px" }}>
                      {userLogged.name} {userLogged.surname}
                    </span>
                  </div>
                  <Card
                    style={{
                      fontSize: "12px",
                      textAlign: "center",
                      margin: "2px auto",
                    }}>
                    {newRoleConfig?.type ? "MODO DE PRUEBAS" : ""}
                  </Card>
                  <Card
                    style={{
                      fontSize: "12px",
                      textAlign: "center",
                      margin: "2px auto",
                    }}>
                    {newRoleConfig?.type
                      ? newRoleConfig?.type
                      : userLogged?.full_rol_name}
                  </Card>
                </div>
              </div>
            ) : (
              <div className="userSkeleton">
                <Skeleton variant="circular" width={50} height={50} />
              </div>
            )}

            <div
              className={`sidebar-toggle-btn desktop-toggle ${collapsed ? "collapsed" : ""}`}
              onClick={() => handleCollapseSidebar()}>
              {collapsed ? <ChevronRight /> : <ChevronLeft />}
            </div>
          </div>
        </SidebarHeader>

        <SidebarContent
          className="sideBarContentClass"
          sx={{ position: "relative" }}>
          {menuConfig.map((section) => {
            if (section.type === "submenu") {
              const visibleItems = section.items.filter((item) => {
                if (!item.module || !item.action) return true;
                // console.log(
                //   can(item.module, item.action),
                //   item.module,
                //   item.action,
                // );
                return can(item.module, item.action);
              });

              if (!visibleItems.length) return null;

              return (
                <Menu key={section.id} iconShape="circle">
                  <SubMenu
                    title={intl.formatMessage({ id: section.label })}
                    icon={section.icon}
                    open={openSubMenu === section.id}
                    onClick={() =>
                      setOpenSubMenu((prev) =>
                        prev === section.id ? null : section.id,
                      )
                    }>
                    {visibleItems.map((item) => (
                      <MenuItem key={item.id}>
                        {renderNavOrAnchor(item)}
                      </MenuItem>
                    ))}
                  </SubMenu>
                </Menu>
              );
            }

            return (
              <Menu key={section.id} iconShape="circle">
                {section.items
                  .filter((item) => {
                    if (!item.module || !item.action) return true;
                    return can(item.module, item.action);
                  })
                  .map((item) => (
                    <MenuItem key={item.id} icon={item.icon}>
                      {renderNavOrAnchor(item)}
                    </MenuItem>
                  ))}
              </Menu>
            );
          })}
          <div
            className="sidebar-btn-wrapper"
            style={{
              padding: "20px 24px",
              position: "absolute",
              bottom: "10px",
              right: 0,
              left: 0,
            }}>
            {userData?.isSuperAdmin && collapsed ? (
              <AdminPanelSettings
                onClick={() => setOpenNewUserMode(true)}
                sx={{ cursor: "pointer" }}
              />
            ) : (
              userData?.isSuperAdmin && (
                <Box sx={{ width: "100%" }}>
                  <Button
                    variant="contained"
                    fullWidth
                    color="info"
                    size="small"
                    onClick={() => setOpenNewUserMode(true)}
                    startIcon={<AdminPanelSettings />}>
                    Modo pruebas
                  </Button>

                  {newRoleConfig && (
                    <Button
                      variant="contained"
                      color="error"
                      fullWidth
                      size="small"
                      onClick={exitTestMode}
                      disabled={!newRoleConfig}
                      sx={{ mt: "5px" }} // <- Aquí
                    >
                      Salir del modo prueba
                    </Button>
                  )}
                </Box>
              )
            )}
          </div>
        </SidebarContent>

        <SidebarFooter style={{ textAlign: "center" }}>
          <div className="sidebar-btn-wrapper" style={{ padding: "20px 24px" }}>
            {collapsed ? (
              <HelpRounded
                onClick={() => setOpenDialog(true)}
                sx={{ cursor: "pointer" }}
              />
            ) : (
              <Button
                sx={{
                  color: "white",
                  border: "1px solid",
                  borderRadius: "4px",
                  width: "100%",
                }}
                onClick={() => setOpenDialog(true)}
                startIcon={<HelpRounded />}>
                ATAJOS
              </Button>
            )}
          </div>

          {/* DIALOGS  */}
          <ChangeMode
            open={openNewUserMode}
            setOpen={setOpenNewUserMode}
            onClose={() => setOpenNewUserMode(false)}
            currentRoleId={userData?.user}
          />

          <Dialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            maxWidth="sm"
            fullWidth>
            <DialogTitle>
              Atajos de ayuda
              <IconButton
                aria-label="close"
                onClick={() => setOpenDialog(false)}
                sx={{ position: "absolute", right: 8, top: 8 }}>
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {shortcuts.map((d) => (
                <div key={d.id} style={{ textAlign: "center", padding: "5px" }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      navigate(d.path);
                      setOpenDialog(false);
                    }}>
                    {d.label}
                  </Button>
                </div>
              ))}
            </DialogContent>
          </Dialog>
        </SidebarFooter>
      </ProSidebar>
      {/* Desktop chevron (visible on md+) */}
      {/* <DesktopToggle /> */}

      {/* Mobile hamburger (visible on < md) */}
      {/* <MobileToggle /> */}
    </div>
  );
}

export default Aside;
